import React from 'react';

import { noop } from '@nfctron/nexus-core';
import { UiButton, type UiButtonProps, cn, useContextRequired, useFormat } from '@nfctron/nexus-react';
import { ApplePayLogo, CzechiaFlagIcon, EuFlagIcon, GooglePayShortLogo, McClickToPayWhiteLogo } from '@nfctron/nexus-react-svg';

import { Indicator } from '@mantine/core';
import { LockIcon, ShoppingCartIcon } from 'lucide-react';
import { useRouter } from 'next/router';

import { useApiClient } from '@root/features/api-client/api-client.context';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { openModal } from '@root/lib/modals/utils/openModal';
import { type AtomComponent } from '@root/lib/types/components';

import { EventCartPageContext } from '../../components/CartContext';
import { CartStorePaymentOptionsSlice, useCartOrderApi, useCartStore } from '../../stores/cartStore';

/**
 * Event/Cart/CheckoutViewFooter atom component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const CheckoutViewFooterAtom: AtomComponent = () => {
	const format = useFormat();
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, CheckoutViewFooterAtom.displayName);
	const { event } = useContextRequired(EventCartPageContext);
	const cartConfig = useCartStore((s) => s.cartConfig);
	const router = useRouter();
	const { passApi } = useApiClient();
	const activePaymentOptionId = useCartStore((s) => s.activePaymentOptionId);
	const cartedTickets = useCartStore((s) => s.cartedTickets);
	const cartTotal = useCartStore((s) => s.getCartTotal());
	const { createOrderApi, canCreateOrder } = useCartOrderApi({
		async onOrderCreated(params) {
			const redirectUrl = `/event/${event.organizer.slug}/${event.slug}/order?orderId=${params.orderId}&fromQueue=true&enforceMethod=${params.enforcePaymentMethod}`;
			/** prefetch event/order page */
			await router.prefetch(redirectUrl);
			/** open order create success modal */
			openModal('orderCreateSuccess', {
				centered: true,
				closeOnClickOutside: false,
				closeOnEscape: false,
				withCloseButton: false,
				innerProps: {
					onButtonClick: async ({ id, closeModal }) => {
						await router.push(redirectUrl);
						closeModal(id);
					},
				},
			});
			/** ensure zero-amount order payment */
			if (params.amount === 0) {
				console.debug('Detected zero-amount order, ensuring its payment right away');
				/** call silently order pay (zero-amount) */
				await passApi
					.request<any, any>({
						path: `/pay/order/${params.orderId}`,
						method: 'GET',
						format: 'json',
					})
					.catch(noop);
			}
			/** push to event/order page */
			await router.push(redirectUrl);
		},
	});

	const { className: buttonClassName, ...restButtonProps } = {
		size: 'default',
		className: 'w-full lg:w-unset min-w-[200px] text-md h-12',
		loading: createOrderApi.isPending,
		onClick: async () => createOrderApi.mutateAsync(),
		disabled: !canCreateOrder || cartConfig.isDemoCart,
		...(cartTotal.total === 0 && {
			variant: 'primary',
			leftSection: <ShoppingCartIcon />,
			// FIXME: i18n
			children: 'Dokončit objednávku',
		}),
	} satisfies UiButtonProps;

	return (
		<div className="mt-4.5 flex h-full w-full flex-col justify-around gap-3 lg:mt-7 lg:flex-row lg:items-center lg:justify-between xl:mt-0">
			<div className="flex items-center justify-between gap-2 lg:flex-col lg:items-start">
				{/* label */}
				<div className="flex items-center gap-4">
					{/* carted ticket count */}
					<Indicator label={<span className="text-xs font-bold">{format.number(cartedTickets.length)}</span>} size="16px" color="lucky-point">
						<ShoppingCartIcon className="size-5" />
					</Indicator>
					{/* total sum heading */}
					<span className="text-lg font-medium text-zinc-900">{t('orderTotalSum.text')}</span>
				</div>

				{/* price */}
				<div>
					{/* total sum for the whole order */}
					<span className="text-lucky-point-500 ml-auto flex items-end gap-2 text-2xl font-bold md:text-4xl">
						{/* original price (if different) */}
						{cartTotal.total !== cartTotal.originalTotal && (
							<span className="order-0 text-sm font-normal text-zinc-500 line-through lg:order-1 lg:text-base">
								{format.price(cartTotal.originalTotal, cartConfig.currencyId)}
							</span>
						)}
						{/* price */}
						{format.price(cartTotal.total, cartConfig.currencyId)}
					</span>
					{/* sum vat disclaimer */}
					<span className="hidden text-sm text-zinc-500 lg:block">{t('totalSum.disclaimerText')}</span>
				</div>
			</div>

			{/* pay button */}
			{
				(
					{
						/** disabled/unselected button */
						'': (
							<UiButton variant="secondary" className={cn('', buttonClassName)} leftSection={<LockIcon />} {...restButtonProps} disabled>
								{t('selectMethodButtonLabel')}
							</UiButton>
						),
						/** Apple Pay method */
						[CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.APPLE_PAY]: (
							<UiButton
								variant="unstyled"
								className={cn('rounded-lg bg-black font-medium text-white', buttonClassName)}
								// eslint-disable-next-line react/no-children-prop
								children={
									<div className="flex items-center gap-1">
										<span className="leading-none">{t('applePayButtonLabel')}</span>
										<ApplePayLogo className="h-4 w-12 fill-white" />
									</div>
								}
								{...restButtonProps}
							/>
						),
						/** Google Pay method */
						[CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.GOOGLE_PAY]: (
							<UiButton
								variant="outlined"
								className={cn('rounded-lg font-medium text-zinc-700 shadow-xs', buttonClassName)}
								// eslint-disable-next-line react/no-children-prop
								children={
									<div className="flex items-center gap-1">
										<span className="leading-none">{t('googlePayButtonLabel')}</span>
										<GooglePayShortLogo className="h-4 w-12" />
									</div>
								}
								{...restButtonProps}
							/>
						),
						/** mastercard click to pay method */
						[CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.CLICK_TO_PAY]: (
							<UiButton
								variant="unstyled"
								className={cn('rounded-lg bg-black text-white', buttonClassName)}
								// eslint-disable-next-line react/no-children-prop
								children={
									<div className="flex items-center gap-2">
										<span className="leading-none">{t('clickToPayButtonLabel')}</span>
										<McClickToPayWhiteLogo className="h-4 w-14" />
									</div>
								}
								{...restButtonProps}
							/>
						),
						/** basic credit card payment */
						[CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.CREDIT_CARD]: (
							<UiButton
								variant="primary"
								className={cn('rounded-lg', buttonClassName)}
								leftSection={<LockIcon />}
								// eslint-disable-next-line react/no-children-prop
								children={t('creditCardButtonLabel')}
								{...restButtonProps}
							/>
						),
						/** bank transfer method */
						[CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.BANK_TRANSFER_CZK]: (
							<UiButton
								variant="primary"
								className={cn('rounded-lg', buttonClassName)}
								leftSection={<CzechiaFlagIcon />}
								// eslint-disable-next-line react/no-children-prop
								children={t('bankTransferCzkButtonLabel')}
								{...restButtonProps}
							/>
						),
						[CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.BANK_TRANSFER_EUR]: (
							<UiButton
								variant="primary"
								className={cn('rounded-lg', buttonClassName)}
								leftSection={<EuFlagIcon />}
								/* eslint-disable-next-line react/no-children-prop */
								children={t('bankTransferEurButtonLabel')}
								{...restButtonProps}
							/>
						),
					} as Partial<Record<CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID | '', React.ReactNode>>
				)[activePaymentOptionId ?? '']
			}
		</div>
	);
};
CheckoutViewFooterAtom.displayName = 'Event/Cart/CheckoutViewFooterAtom';
